import { Action } from 'redux'
import { put, takeLatest, all, take, select } from 'redux-saga/effects'
import { rootActions } from '@app-store/slices'
import axios from '@utils/request'
import { IListQueryPaginationPayload } from '@app-store/slices/companies/list'
import { RootState } from '@app-store/store'
import routes from '@utils/constants/routes'
import { history } from '@components/App'
import { useUser } from '../../../hooks'
import forOwn from 'lodash/forOwn'
import { setEventCookie } from '@utils/helpers/authHelper'

interface TaskAction extends Action {
    payload: IListQueryPaginationPayload
}

const onboarding = rootActions.companies.onboardingExhibitor
const { onRequest: getPreSignedUploadURL } = rootActions.contacts.getPreSignedUploadURL
const { onRequest: getCompanyPreSignedUploadURL } = rootActions.companies.getPreSignedUploadURL
const { onRequest: fileUpload } = rootActions.contacts.fileUpload
const { onRequest: fileUploadCompany } = rootActions.companies.fileUpload
const { onRequest: getContact } = rootActions.companies.contactProfile

function* saveSaga({ payload }) {
    try {
        let response
        yield put(getContact({ eventId: payload.eventID }))
        yield take('hub/companies/contact-profile/onSuccess')
        const contact = yield select((store: RootState) => store.companies.contactProfile.data)
        if (payload.isAdmin) {
            response = yield all([
                axios.put(
                    `/companies/${payload.companyID}`,
                    { ...payload.company, logo: payload.logo },
                    {
                        headers: {
                            'event-id': payload.eventID
                        }
                    }
                ),
                axios.post(
                    `/meeting/unavailable`,
                    { ...payload.unavailableCalendar },
                    {
                        headers: {
                            'event-id': payload.eventID
                        }
                    }
                ),
                axios.put(
                    `/companies/contacts/${contact._id}`,
                    { ...payload.contact, avatar: payload.avatar },
                    {
                        headers: {
                            'event-id': payload.eventID
                        }
                    }
                )
            ])
        } else {
            response = yield all([
                axios.post(
                    `/meeting/unavailable`,
                    { ...payload.unavailableCalendar },
                    {
                        headers: {
                            'event-id': payload.eventID
                        }
                    }
                ),
                axios.put(
                    `/companies/contacts/${contact._id}`,
                    { ...payload.contact },
                    {
                        headers: {
                            'event-id': payload.eventID
                        }
                    }
                )
            ])
        }

        if (response) {
            setEventCookie(payload.eventID)
            history.push(routes.organizer._)
            yield put(rootActions.organizer.eventDetails.view.setFinishModal(true))
            yield put(onboarding.onSuccess(response))
        }
    } catch (error) {
        yield put(onboarding.onFailure(error))
    }
}

function* uploadImage({ payload }) {
    try {
        yield put(getPreSignedUploadURL())
        yield take('hub/contacts/get-presigned-upload-URL/onSuccess')
        const { file } = yield select((store: RootState) => store.contacts.getPreSignedUploadURL.data)
        yield put(fileUpload(payload.image[0]))
        yield take('hub/contacts/file-upload/onSuccess')
        yield put(
            rootActions.companies.onboardingExhibitor.set({
                type: payload.name,
                value: file
            })
        )
    } catch (e) {
        // console.log(e)
    }
}

function* uploadImageCompany({ payload }) {
    try {
        yield put(getCompanyPreSignedUploadURL())
        yield take('hub/companies/get-presigned-upload-URL/onSuccess')
        const { file } = yield select((store: RootState) => store.companies.getPreSignedUploadURL.data)
        yield put(fileUploadCompany(payload.image[0]))
        yield take('hub/companies/file-upload/onSuccess')
        yield put(
            rootActions.companies.onboardingExhibitor.set({
                type: payload.name,
                value: file
            })
        )
    } catch (e) {
        // console.log(e)
    }
}

function* saveITP() {
    const { user } = useUser()
    const data = yield select((store: RootState) => store.companies.onboardingExhibitor)
    let countries = []
    const { selectedArea, selectedCountries } = data.locationItp
    forOwn(selectedCountries, value => {
        countries = [...countries, ...value]
    })
    try {
        const response = yield axios.post(
            `/companies/itps`,
            {
                ownerCompany: user.company,
                locations: [...selectedArea],
                countries: [...countries],
                companySize: [...data.companySize],
                type: [...data.type],
                itpType: data.itpType[0],
                positions: [...data.positions],
                tags: data.tags.map(item => item._id || item),
                clientWebsiteExample: data.clientWebsiteExample,
                competitorWebsiteExample: data.competitorWebsiteExample
            },
            {
                headers: {
                    'event-id': data.event._id
                }
            }
        )

        if (response) {
            yield put(onboarding.onSaveITPSucces())
            yield put(rootActions.users.profile.onRequest({}))
            yield take('hub/user/profile/onSuccess')
            history.push(routes.exhibitor.onboarding.categories._)
        }
    } catch (error) {
        yield put(onboarding.onSaveITPFailure())
    }
}

const { onRequest: getCompany } = rootActions.companies.oneCompany

function* getProfile() {
    const { user } = useUser()
    const data = yield select((store: RootState) => store.companies.onboardingExhibitor)
    const companyId = user.eventInfos.find(x => x.eventId === data.event._id)?.companyId
    try {
        yield put(getContact({ eventId: data.event._id }))
        yield take('hub/companies/contact-profile/onSuccess')
        const contact = yield select((store: RootState) => store.companies.contactProfile.data)
        const profile = {
            profileImage: contact.avatar,
            firstname: contact.firstname,
            lastname: contact.lastname,
            linkedin: contact.linkedin,
            twitter: contact.twitter,
            company: contact.company,
            email: contact.email,
            conversationStarter: contact.conversationStarter,
            description: contact.description,
            mobile: contact.mobile
        }
        let company = {}
        if (data.isAdmin && companyId) {
            yield put(getCompany({ companyId: companyId, eventId: data.event._id }))
            yield take('hub/companies/one-company/onSuccess')
            const oneCompany = yield select((store: RootState) => store.companies.oneCompany.data)
            company = {
                companyImage: null,
                company_name: oneCompany.name,
                company_website: oneCompany.website,
                company_tel: oneCompany.tel,
                company_email: oneCompany.email,
                company_country: oneCompany.country,
                company_city: oneCompany.city?._id || oneCompany.city,
                company_address: oneCompany.address,
                company_keywords1:
                    Array.isArray(oneCompany.keyIndicators) && oneCompany.keyIndicators[0]
                        ? oneCompany.keyIndicators[0]
                        : '',
                company_keywords2:
                    Array.isArray(oneCompany.keyIndicators) && oneCompany.keyIndicators[1]
                        ? oneCompany.keyIndicators[1]
                        : '',
                company_keywords3:
                    Array.isArray(oneCompany.keyIndicators) && oneCompany.keyIndicators[2]
                        ? oneCompany.keyIndicators[2]
                        : '',
                company_brief: oneCompany.brief
            }
        }
        if (contact) {
            yield put(onboarding.onGetProfileSucces(Object.assign(profile, company)))
        }
    } catch (error) {
        // console.log(error)
        yield put(onboarding.onGetProfileFailure())
    }
}

function* listSaga(): any {
    yield all([
        takeLatest<TaskAction>(onboarding.onRequest, saveSaga),
        takeLatest<TaskAction>(onboarding.onSaveITP, saveITP),
        takeLatest<TaskAction>(onboarding.onGetProfile, getProfile),
        takeLatest<TaskAction>(onboarding.onUploadImage, uploadImage),
        takeLatest<TaskAction>(onboarding.onUploadLogoCompany, uploadImageCompany)
    ])
}

export default listSaga
